/**
 * AppBar - Top application bar (fixed, with glassmorphism backdrop)
 */
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  StatusBar,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Icon, IconName } from './Icon';
import { LogoIcon } from './Logo';
import { colors, spacing, APP_BAR_HEIGHT } from '../theme';
import type { RootStackParamList } from '../types/navigation';

interface AppBarAction {
  icon: IconName;
  onPress?: () => void;
  href?: string;
}

interface AppBarProps {
  title?: string;
  subtitle?: string;
  showLogo?: boolean;
  logoIcon?: IconName;
  actions?: AppBarAction[];
  onBack?: () => void;
  showBack?: boolean;
  avatarSrc?: string;
  translucent?: boolean;
}

export const AppBar: React.FC<AppBarProps> = ({
  title = 'eQuote',
  showLogo = true,
  logoIcon = 'calculate',
  actions = [],
  onBack,
  showBack = false,
  avatarSrc,
  translucent = true,
}) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const topPad = Platform.OS === 'web' ? 0 : insets.top;

  // Get active route name from navigation state
  const navState = (navigation as any).getState?.();
  const screenName = navState?.routes?.[navState?.index ?? 0]?.name as keyof RootStackParamList | undefined;

  if (screenName === 'M01SplashScreen' || screenName === 'M02LoginScreen') {
    return null;
  }

  let activeTitle = title;
  let activeShowBack = showBack;
  let activeShowLogo = showLogo;
  let activeOnBack = onBack;

  if (screenName) {
    const configMap: Record<string, { title: string; showBack: boolean; showLogo: boolean }> = {
      M03HomeScreen: { title: 'Trang chủ', showBack: false, showLogo: true },
      M04ProductListScreen: { title: 'Sản phẩm', showBack: true, showLogo: false },
      M05ProductDetailScreen: { title: 'Chi tiết sản phẩm', showBack: true, showLogo: false },
      M06InputCustomerScreen: { title: 'Tạo minh họa', showBack: true, showLogo: false },
      M07ResultScreen: { title: 'Kết quả minh họa', showBack: true, showLogo: false },
      M08CompareScreen: { title: 'So sánh phương án', showBack: true, showLogo: false },
      M09PdfScreen: { title: 'Tài liệu PDF', showBack: true, showLogo: false },
      M10SaveCustomerScreen: { title: 'Lưu khách hàng', showBack: true, showLogo: false },
      M11HistoryScreen: { title: 'Lịch sử', showBack: false, showLogo: true },
      M12ProfileScreen: { title: 'Cá nhân', showBack: false, showLogo: true },
    };

    const config = configMap[screenName];
    if (config) {
      activeTitle = config.title;
      activeShowBack = config.showBack;
      activeShowLogo = config.showLogo;
    }
  }

  if (!activeOnBack && activeShowBack) {
    activeOnBack = () => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      }
    };
  }

  return (
    <>
      <StatusBar
        barStyle="dark-content"
        backgroundColor={translucent ? 'transparent' : colors.surface}
        translucent={translucent}
      />
      <View
        style={[
          styles.bar,
          {
            paddingTop: topPad,
            height: APP_BAR_HEIGHT + topPad,
            backgroundColor: translucent
              ? 'rgba(255,255,255,0.90)'
              : colors.surface,
          },
        ]}
      >
        <View style={styles.inner}>
          {/* Left section */}
          <View style={styles.left}>
            {activeShowBack && (
              <TouchableOpacity
                style={styles.iconBtn}
                onPress={activeOnBack}
                activeOpacity={0.7}
                accessibilityRole="button"
                aria-label="Go back"
              >
                <Icon name="arrow_back" size={24} color={colors.primary} />
              </TouchableOpacity>
            )}
            <View style={styles.brand}>
              {activeShowLogo && <LogoIcon size={30} />}
              <Text style={styles.title}>{activeTitle}</Text>
            </View>
          </View>

          {/* Right section */}
          <View style={styles.actions}>
            {actions.map((action, i) => (
              <TouchableOpacity
                key={i}
                style={styles.iconBtn}
                onPress={action.onPress}
                activeOpacity={0.7}
              >
                <Icon name={action.icon} size={24} color={colors.onSurfaceVariant} />
              </TouchableOpacity>
            ))}
            {avatarSrc && (
              <View style={styles.avatarWrap}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>M</Text>
                </View>
              </View>
            )}
          </View>
        </View>
      </View>
    </>
  );
};


const styles = StyleSheet.create({
  bar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    backgroundColor: 'rgba(255,255,255,0.95)',
    // @ts-ignore backdropFilter is a web-only property
    backdropFilter: 'blur(20px)',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(0,51,102,0.08)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 3,
  },
  inner: {
    height: APP_BAR_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing[3],
  } as ViewStyle,
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
  } as ViewStyle,
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  } as ViewStyle,
  title: {
    fontFamily: "'Be Vietnam Pro', sans-serif",
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: -0.01,
    color: colors.primary,
  } as TextStyle,
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
  } as ViewStyle,
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  } as ViewStyle,
  avatarWrap: {
    marginLeft: spacing[1],
  } as ViewStyle,
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primaryFixed,
    borderWidth: 2,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  avatarText: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    fontWeight: '700',
    color: colors.primary,
  } as TextStyle,
});
